import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import MessageBoardApi from '../../apis/messageboard';
import { resettingMessageBoardLoader } from '../slices/messageboard';
import { RootState } from '../../redux/store';
import { AddMessageBoardParams, AddOrRemoveAssingeeParams } from '../../params/redux/messageboard';

interface CreateMessageBoardResponseInterface {
    success?: 0 | 1;
    msg?: string;
    messageBoard?: {
        agency_id?: number;
        client_id?: number;
        name?: string;
        description?: null | string;
        created_by?: number;
        updated_at?: string;
        created_at?: string;
        id?: number;
    };
    token?: string;
}

export const fetchMessageBoardDetailThunk = createAsyncThunk(
    'message-board/detail',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await MessageBoardApi.fetchMessageBoardDetail(state.app.token, id);
            if (response?.success === 1) {
                return {
                    messageBoard: response?.messageBoard,
                    is_owner: response?.is_owner,
                    assignee: response?.assignee,
                    conversations: response?.conversations
                };
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingMessageBoardLoader());
        }
    }
);

export const fetchMessageBoardThunk = createAsyncThunk(
    'message-board/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await MessageBoardApi.fetchMessageBoard(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.messageBoard;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingMessageBoardLoader());
        }
    }
);

export const createMessageBoardConversationThunk = createAsyncThunk(
    'message-board/create-conversation',
    async (data: { message: string, file: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await MessageBoardApi.createMessageBoardConversation(
                state.app.token,
                state.messageboard.selectedMessageBoard?.id || 0,
                data.message,
                data.file
            );
            if (response?.success === 1) {
                await dispatch(fetchMessageBoardDetailThunk(state.messageboard.selectedMessageBoard?.id || 0)).unwrap();
                return response?.true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingMessageBoardLoader());
        }
    }
);

export const createMessageBoardThunk = createAsyncThunk(
    'message-board/create',
    async (data: AddMessageBoardParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const passedData: AddMessageBoardParams = {
                ...data,
                client_id: data.client_id || 0
            };
            const response: CreateMessageBoardResponseInterface = await MessageBoardApi.createMessageBoard(
                state.app.token,
                passedData
            );
            if (response?.success === 1) {
                await dispatch(fetchMessageBoardThunk(data.client_id || 0)).unwrap();
                await dispatch(fetchMessageBoardDetailThunk(response?.messageBoard?.id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingMessageBoardLoader());
        }
    }
);

export const addRemoveAssigneeMessageBoardThunk = createAsyncThunk(
    'message-board/add-remove-assignee',
    async ({ data, client_id }: {data: AddOrRemoveAssingeeParams, client_id: number}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await MessageBoardApi.addOrRemoveAssigneeOfMessageBoard(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchMessageBoardThunk(client_id)).unwrap();
                await dispatch(fetchMessageBoardDetailThunk(state.messageboard.selectedMessageBoard?.id || 0)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingMessageBoardLoader());
        }
    }
);

